/**
 * Контроль підписання — реєстр непідписаних документів (0429, 2026-08-17).
 * Показує проведені документи контрольованих типів без ЕЦП-підпису
 * контрагента і без ручної позначки (скан/паперовий оригінал).
 * Мігровані з 1С документи не контролюються.
 *
 * Канон Норми #29 (2026-08-22): .card + InfiniteDocGrid (серверна
 * пагінація/сортування) + фільтри рядком; «Усі організації» — порожній
 * фільтр org (backend віддає всі доступні org'и юзера).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { signControl, type UnsignedRow } from '@/api/signControl';
import SearchSelect from '@/components/SearchSelect';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useOrgFilter } from '@/store/defaults';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';

const EXTRA_NAMES: Record<string, string> = {
  'ref.contracts': 'Договори з контрагентами',
};
const typeName = (t: string) => DOC_TABLE_NAMES_UK[t] ?? EXTRA_NAMES[t] ?? t;

// Маршрути «відкрити документ» — спільна карта (ч.793: shared/utils/docRoutes).

export default function SignControlPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const confirmDlg = useConfirm();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Порожній orgId = «Усі організації» (backend віддає всі доступні).
  const [orgId, setOrgId] = useOrgFilter();
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);
  const [fltType, setFltType] = useState('');
  const [fltContractor, setFltContractor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Метадані конверта (enabled/paper/types) — оновлюються кожною сторінкою.
  const [meta, setMeta] = useState<{
    enabled: boolean; paper: boolean; types: string[] } | null>(null);
  const refreshRef = useRef<(() => void) | null>(null);
  // Зміна фільтрів → скинути мету, щоб грід повернувся на екран і
  // перезапросив сторінку (коли попередня org була з вимкненим контролем).
  useEffect(() => { setMeta(null); }, [orgId]);

  const { datasource, total } = useInfiniteGridDatasource<UnsignedRow>(
    async (limit, offset, sort) => {
      const p = await signControl.unsigned({
        organization_id: orgId || undefined,
        document_type: fltType || undefined,
        contractor_id: fltContractor || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        limit, offset,
      });
      setMeta({ enabled: p.enabled, paper: !!p.paper_original,
        types: p.types ?? [] });
      return { items: p.items, total: p.total };
    },
    [orgId, fltType, fltContractor, dateFrom, dateTo],
  );

  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  // Кнопка «паперовий оригінал» — ЛИШЕ коли режим увімкнено на картці орг
  // (радіо у «списку документів контролю»); при «усі організації» —
  // per-row прапор paper_original визначає видимість кнопки у рядку.
  const paperEnabled = !!meta?.paper;
  const types = meta?.types ?? [];

  const markPaper = async (r: UnsignedRow) => {
    const ok = await confirmDlg(
      `Позначити «${typeName(r.document_type)} ${r.number ?? ''}» як паперовий `
      + 'оригінал (без скану) і зняти з контролю підписання?');
    if (!ok) return;
    try {
      await signControl.mark(r.organization_id, {
        document_type: r.document_type, document_id: r.document_id,
        kind: 'PAPER' });
      refreshRef.current?.();
      qc.invalidateQueries({ queryKey: ['sign-control-statuses'] });
    } catch { window.alert('Не вдалося поставити позначку'); }
  };

  // colId колонок = ключі whitelist серверного сортування
  // (date/type/number/contractor/organization); дія — sortable:false.
  const cols = useMemo<ColDef<UnsignedRow>[]>(() => [
    {
      colId: 'date', field: 'date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value
        ? new Date(p.value).toLocaleDateString('uk-UA') : '—',
    },
    {
      colId: 'type', field: 'document_type', headerName: 'Документ', width: 220,
      valueFormatter: (p) => p.value ? typeName(p.value) : '',
    },
    {
      // Номер — ПОСИЛАННЯМ на документ (рішення власника 2026-08-19;
      // колонки «Сума» і дій прибрано — відкриття тепер через номер,
      // «паперовий оригінал» ставиться перемикачем у списку документів).
      colId: 'number', field: 'number', headerName: '№', width: 140,
      cellRenderer: (p: { data?: UnsignedRow }) => {
        const r = p.data;
        if (!r) return '';
        return DOC_ROUTES[r.document_type] ? (
          <a style={{ cursor: 'pointer' }} title="Відкрити документ"
            onClick={() => nav(DOC_ROUTES[r.document_type]!(r.document_id))}>
            {r.number ?? 'відкрити'}
          </a>
        ) : (r.number ?? '—');
      },
    },
    {
      colId: 'contractor', field: 'contractor_name', headerName: 'Контрагент',
      flex: 1, minWidth: 180,
      valueFormatter: (p) => p.value ?? '—',
    },
    {
      colId: 'organization', field: 'organization_id',
      headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    ...(paperEnabled ? [{
      colId: 'paper', headerName: '', width: 170, sortable: false,
      cellRenderer: (p: { data?: UnsignedRow }) => p.data?.paper_original ? (
        <button style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}
          title="Зняти з контролю без скану — паперовий оригінал у наявності"
          onClick={() => void markPaper(p.data!)}>
          паперовий оригінал
        </button>
      ) : null,
    } satisfies ColDef<UnsignedRow>] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [paperEnabled, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          Монітор підписань<TitleTotal total={total} />
        </h2>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
        marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={fltType} onChange={(e) => setFltType(e.target.value)}
          style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Тип документа — усі</option>
          {types.map((t) => <option key={t} value={t}>{typeName(t)}</option>)}
        </select>
        <div style={{ width: 220, flex: '0 0 auto' }}>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={fltContractor}
            onChange={setFltContractor}
            placeholder="Контрагент — усі"
          />
        </div>
        {fltContractor && (
          <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            onClick={() => setFltContractor('')}>×</button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>з</span>
          <div style={{ width: 140 }}>
            <DatePopover value={dateFrom} onChange={setDateFrom} />
          </div>
          <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>по</span>
          <div style={{ width: 140 }}>
            <DatePopover value={dateTo} onChange={setDateTo} />
          </div>
          {(dateFrom || dateTo) && (
            <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
              onClick={() => { setDateFrom(''); setDateTo(''); }}>×</button>
          )}
        </div>
      </div>

      {meta && !meta.enabled ? (
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {orgId
            ? 'Контроль підписання вимкнено для цієї організації — увімкніть '
              + 'його на картці організації (розділ «Облікові модулі»).'
            : 'Контроль підписання не увімкнено для жодної з ваших '
              + 'організацій — увімкніть його на картці організації '
              + '(розділ «Облікові модулі»).'}
        </p>
      ) : (
        <InfiniteDocGrid<UnsignedRow>
          datasource={datasource}
          columnDefs={cols}
          storageKey="sign-control"
          refreshRef={refreshRef}
        />
      )}
    </div>
  );
}
